// Editorial landing-page choreography, not the product's score calculation.
// No event exposes a contribution or a public weighting formula.
export const GAMEPOWER_PHASES = Object.freeze({
  pathStart: 0.07,
  supplements: 0.86,
  complete: 0.92,
  matchdayStart: 0.935,
  hold: 0.96,
  revealLead: 0.065,
  whiteLead: 0.018,
  colorDuration: 0.018,
});

export const GAMEPOWER_EVENTS = Object.freeze([
  { id: "breakfast", label: "Frühstück", icon: "utensils", at: 0.16 },
  { id: "training", label: "Training", icon: "dumbbell", at: 0.30 },
  { id: "lunch", label: "Mittagessen", icon: "utensils", at: 0.45 },
  { id: "recovery", label: "Recovery", icon: "heart", at: 0.59 },
  { id: "dinner", label: "Abendessen", icon: "utensils", at: 0.73 },
  { id: "supplements", label: "Supplements", icon: "pill", at: GAMEPOWER_PHASES.supplements },
].map(Object.freeze));

const clamp = (value) => Math.max(0, Math.min(1, value));
const between = (value, start, end) => clamp((value - start) / (end - start));
const smooth = (value) => value * value * (3 - 2 * value);

// Unequal illustrative ramps follow contact with a measure, with a deliberate
// 98% plateau before the last supplement. These values are not event weights.
const percentageStops = [
  [0, 0], [0.16, 0], [0.215, 11],
  [0.30, 11], [0.365, 36], [0.45, 36], [0.51, 53],
  [0.59, 53], [0.66, 77], [0.73, 77], [0.81, 98],
  [GAMEPOWER_PHASES.supplements, 98], [GAMEPOWER_PHASES.complete, 100], [1, 100],
];

export function getGamepowerScrollState(rawProgress) {
  const progress = Number.isFinite(rawProgress) ? clamp(rawProgress) : 0;
  const next = percentageStops.findIndex(([position]) => position >= progress);
  const [start, from] = percentageStops[Math.max(0, next - 1)];
  const [end, to] = percentageStops[Math.max(0, next)];
  const energy = start === end ? from : from + (to - from) * smooth(between(progress, start, end));
  const events = GAMEPOWER_EVENTS.map((event) => {
    const opacity = smooth(between(progress, event.at - GAMEPOWER_PHASES.revealLead, event.at - GAMEPOWER_PHASES.whiteLead));
    const activation = smooth(between(progress, event.at, event.at + GAMEPOWER_PHASES.colorDuration));
    return { ...event, opacity, activation, visible: opacity > 0, activated: progress >= event.at };
  });
  const matchdayOpacity = smooth(between(progress, GAMEPOWER_PHASES.matchdayStart, GAMEPOWER_PHASES.hold));
  return {
    progress,
    percentage: Math.round(energy),
    energy: energy / 100,
    activePathProgress: between(progress, GAMEPOWER_PHASES.pathStart, GAMEPOWER_PHASES.supplements),
    events,
    visibleEvents: events.filter((event) => event.visible).map((event) => event.id),
    activatedEvents: events.filter((event) => event.activated).map((event) => event.id),
    complete: progress >= GAMEPOWER_PHASES.complete,
    matchdayVisible: matchdayOpacity > 0,
    matchdayOpacity,
    phase: progress >= GAMEPOWER_PHASES.hold ? "hold" : progress >= GAMEPOWER_PHASES.complete ? "complete" : "cycle",
  };
}

export function getGamepowerEventPosition(event) {
  return between(event.at, GAMEPOWER_PHASES.pathStart, GAMEPOWER_PHASES.supplements) * 100;
}
